import React from 'react';
import { AlertCircle, Info, Settings } from 'lucide-react';
import WebStepShell from './WebStepShell';
import DeviceMedia from './DeviceMedia';
import type { WebFlowCtx } from './types';

interface Props {
  ctx: WebFlowCtx;
}

const Step3ConnectDeviceWifi: React.FC<Props> = ({ ctx }) => {
  const openWifiSettings = () => {
    // Best-effort attempt to open OS Wi-Fi settings. The browser may show a
    // protocol-handler confirmation dialog — that's expected and out of our
    // control. This is only a convenience shortcut, not part of the main flow.
    try {
      window.open('ms-settings:network-wifi', '_self');
    } catch {
      // Silently ignore — user can open settings manually.
    }
  };

  return (
    <WebStepShell
      step="deviceWifi"
      onHeaderBack={ctx.openExitModal}
      onCancel={ctx.openExitModal}
      secondary={{ label: 'Back', onClick: ctx.goBack, disabled: ctx.loading }}
      primary={{
        label: "I'm Connected",
        onClick: ctx.connectToDeviceWifi,
        loading: ctx.loading,
        loadingLabel: 'Verifying…',
      }}
      media={<DeviceMedia indicator="step1" />}
    >
      <p className="dcfw-eyebrow">SETUP PHASE 3</p>
      <h2 className="dcfw-title">Step 3 of 6: Connect to device Wi-Fi</h2>

      <div className="dcf-step-header" style={{ marginTop: 12 }}>
        Connect your computer to the Smart Eco device Wi-Fi.
      </div>

      <ul className="dcf-instructions">
        <li className="dcf-instructions__item">
          <span className="dcf-instructions__num">1</span>
          <span className="dcf-instructions__text">
            Open your laptop/desktop <strong>Settings &rarr; Wi-Fi</strong>
          </span>
        </li>
        <li className="dcf-instructions__item">
          <span className="dcf-instructions__num">2</span>
          <span className="dcf-instructions__text">
            Select the network starting with <strong>&apos;IAQ_&apos;</strong> or <strong>&apos;IAQ_{ctx.serialNumber}&apos;</strong>
          </span>
        </li>
        <li className="dcf-instructions__item">
          <span className="dcf-instructions__num">3</span>
          <span className="dcf-instructions__text">
            If prompted for a password, enter <strong>&apos;12345678&apos;</strong>
          </span>
        </li>
        <li className="dcf-instructions__item">
          <span className="dcf-instructions__num">4</span>
          <span className="dcf-instructions__text">
            Once connected, come back here and click <strong>&ldquo;I&apos;m Connected&rdquo;</strong>
          </span>
        </li>
      </ul>

      {ctx.deviceWifiError.length > 0 && (
        <div className="dcf-banner dcf-banner--error" style={{ marginBottom: 8 }}>
          <AlertCircle size={20} color="#D2042D" className="flex-shrink-0" />
          <div className="dcf-banner-description">
            <span style={{ fontWeight: 600 }}>Unable to reach the device. Please check:</span>
            <ul className="dcf-error-checklist">
              {ctx.deviceWifiError.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="dcf-banner">
        <Info size={20} color="#EEC94D" className="flex-shrink-0" />
        <div className="dcf-banner-description">
          <span>
            &ldquo;No Internet&rdquo; or &ldquo;Unsecured Network&rdquo; is
            expected. Please allow the connection and stay connected to the
            device Wi-Fi until setup is complete.
          </span>
        </div>
      </div>

      <button type="button" className="dcfw-link-btn" onClick={openWifiSettings}>
        <Settings size={16} /> Open Wi-Fi Settings
      </button>
    </WebStepShell>
  );
};

export default Step3ConnectDeviceWifi;
